import { isEmpty } from './is-empty';
import { type Nullable } from '../types';

type NullishRecord = Record<string, Nullable<unknown>>;
type NullishArray = Array<Nullable<unknown>>;
type Nullish = NullishRecord | NullishArray | Nullable<unknown>;

const isPlainObject = (val: unknown): val is Record<string, unknown> =>
  typeof val === 'object' && val !== null && !Array.isArray(val);

export const excludeNullish = <T extends Nullish>(value: T): NonNullable<T> => {
  if (Array.isArray(value)) {
    const filtered = value
      .map((v) => excludeNullish(v as Nullish))
      .filter((v) => !isEmpty(v) && !(Array.isArray(v) && v.length === 0));
    return filtered as NonNullable<T>;
  }

  if (isPlainObject(value)) {
    const result = Object.entries(value).reduce(
      (acc, [key, val]) => {
        const cleaned = excludeNullish(val as Nullish);
        if (
          !isEmpty(cleaned) &&
          !(Array.isArray(cleaned) && cleaned.length === 0) &&
          !(isPlainObject(cleaned) && Object.keys(cleaned).length === 0)
        ) {
          acc[key] = cleaned;
        }
        return acc;
      },
      {} as Record<string, unknown>,
    );
    return result as NonNullable<T>;
  }

  return value as NonNullable<T>;
};
